import { useState } from "react";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  blogTagListQuery,
  createBlogTagMutation,
  deleteBlogTagMutation,
  updateBlogTagMutation,
  BlogTagListItem,
} from "@/app/Blogs/GraphQL/blogTagQueries";
import { RecordCreateCard } from "@/lib/Components/SideBarList/Cards/RecordCreateCard";
import { BlogTagForm } from "@/app/Blogs/Forms/BlogTagForm";
import { toFormikValidate } from "@/lib/Zod/zod-formik-adapter";
import { blogTagValidation } from "@/app/Blogs/Validation/blogValidation";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/catalyst/button";
import { Dialog, DialogTitle } from "@/components/catalyst/dialog";
import { CreateBlogTagInput, UpdateBlogTagInput } from "@/gql/graphql";

export function BlogTagsScreen() {
  const [editing, setEditing] = useState<BlogTagListItem | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data } = useGqlQuery(blogTagListQuery, {});
  const { mutateAsync: createTag } = useGqlMutation(createBlogTagMutation);
  const { mutateAsync: updateTag } = useGqlMutation(updateBlogTagMutation);
  const { mutateAsync: deleteTag } = useGqlMutation(deleteBlogTagMutation);

  const tags = data?.blogTags ?? [];

  const invalidate = () =>
    queryClient.invalidateQueries({
      predicate: (q) => q.queryKey.includes("BlogTagList"),
    });

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Blog tags</h1>
        <Button onClick={() => setCreateOpen(true)}>New tag</Button>
      </div>

      <Dialog open={createOpen} onClose={setCreateOpen}>
        <DialogTitle>New tag</DialogTitle>
        <RecordCreateCard<CreateBlogTagInput>
          hideTitle
          validate={toFormikValidate(blogTagValidation)}
          onSubmit={async (values) => {
            await createTag({ input: values });
            await invalidate();
            setCreateOpen(false);
          }}
          initialValues={{ name: "", slug: "" }}
        >
          <BlogTagForm />
        </RecordCreateCard>
      </Dialog>

      <ul className="divide-y divide-zinc-200 rounded-md border border-zinc-200 bg-white">
        {tags.map((tag) => (
          <li
            key={tag.id}
            className="flex items-center justify-between px-4 py-3"
          >
            <div>
              <div className="font-medium">{tag.name}</div>
              <div className="text-xs text-zinc-500">{tag.slug}</div>
            </div>
            <div className="flex gap-2">
              <Button outline onClick={() => setEditing(tag)}>
                Edit
              </Button>
              <Button
                outline
                onClick={async () => {
                  if (!confirm("Delete this tag?")) return;
                  await deleteTag({ id: tag.id });
                  await invalidate();
                }}
              >
                Delete
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <Dialog open={editing !== null} onClose={(o) => !o && setEditing(null)}>
        <DialogTitle>Edit tag</DialogTitle>
        {editing ? (
          <RecordCreateCard<UpdateBlogTagInput>
            hideTitle
            validate={
              toFormikValidate(blogTagValidation) as (
                values: UpdateBlogTagInput,
              ) => Promise<Record<string, string> | undefined>
            }
            onSubmit={async (values) => {
              await updateTag({ id: editing.id, input: values });
              await invalidate();
              setEditing(null);
            }}
            initialValues={{ name: editing.name, slug: editing.slug }}
          >
            <BlogTagForm />
          </RecordCreateCard>
        ) : null}
      </Dialog>
    </div>
  );
}
